import { useRef, useState } from "react";
import { useNavigate } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { IconButton } from "@agentsaas/ui";
import { TerminalView } from "../terminal/TerminalView";
import type { ExtraTab } from "../files/EditorArea";
import { ChatPanel } from "../chat/ChatPanel";
import { ApprovalCard } from "../approvals/ApprovalCard";
import { usePendingApprovals } from "../data/queries";
import type { Agent, AgentStatus } from "@agentsaas/protocol";
import { useT } from "../app/i18n";
import { Workspace, useShell } from "../layout/shell";
import { TeamList } from "./TeamList";
import { FilesProvider, useFiles } from "../files/FilesContext";
import { FileTree } from "../files/FileTree";
import { EditorArea } from "../files/EditorArea";

/** "进入某个 agent 的电脑"：文件树、编辑器、终端、对话面板都切换到这个 agent */
export function AgentComputer({ pid, agent, agents }: { pid: string; agent: Agent; agents: Agent[] }) {
  return (
    <FilesProvider aid={agent.id}>
      <ComputerLayout pid={pid} agent={agent} agents={agents} />
    </FilesProvider>
  );
}

export function FilesPane({ aid, status, onOpened, onOpen }: { aid: string; status?: AgentStatus; onOpened?(): void; onOpen?(path: string): void }) {
  const { t } = useT();
  const qc = useQueryClient();
  const { store, active } = useFiles();
  return (
    <>
      <div className="pane-head">
        <span className="grow">{t("fs.files")}</span>
        <IconButton icon="refresh" label={t("fs.refresh")} onClick={() => void qc.invalidateQueries({ queryKey: ["fs", aid, "tree"] })} />
      </div>
      <div className="pane-body">
        <FileTree
          aid={aid}
          status={status}
          activePath={active}
          onOpen={(p) => {
            if (onOpen) return onOpen(p);
            void store.open(p);
            onOpened?.();
          }}
        />
      </div>
    </>
  );
}

function ComputerLayout({ pid, agent, agents }: { pid: string; agent: Agent; agents: Agent[] }) {
  const { t } = useT();
  const navigate = useNavigate();
  const [extraActive, setExtraActive] = useState<string | null>(null);
  const { setDrawerOpen } = useShell();
  const sidebar = (
    <div className="sidebar">
      <div className="pane-head">
        <span className="grow">{t("agent.team")}</span>
      </div>
      <div className="sidebar__team">
        <TeamList pid={pid} agents={agents} />
      </div>
      <div className="sidebar__files" data-close-drawer-scope>
        <FilesPane
          aid={agent.id}
          status={agent.status}
          onOpened={() => {
            setExtraActive(null);
            setDrawerOpen(false);
          }}
        />
      </div>
    </div>
  );
  const terms = useTerminals(agent.id, setExtraActive);
  const { files } = useFiles();
  const approvals = usePendingApprovals(pid).data?.filter((a) => a.agentId === agent.id) ?? [];
  const inline = approvals.length ? (
    <div className="stack" data-testid="inline-approvals">
      {approvals.map((a) => (
        <ApprovalCard key={a.id} approval={a} agentName={agent.name} compact />
      ))}
    </div>
  ) : null;
  const chat = <ChatPanel agent={agent} openFiles={files.map((f) => f.path)} inline={inline} />;
  const main = (
    <EditorArea
      extra={terms.tabs}
      activeExtra={extraActive}
      onActivateExtra={setExtraActive}
      actions={
        <>
          <IconButton icon="sparkles" label={t("agentcap.tab")} onClick={() => navigate(`/p/${pid}/a/${agent.id}/capabilities`)} data-testid="open-capabilities" />
          <IconButton icon="terminal" label={t("term.open")} onClick={terms.open} data-testid="open-terminal" />
        </>
      }
    />
  );
  return <Workspace sidebar={sidebar} main={main} aside={chat} />;
}

/** 终端 tab 管理（每个 tab 一个 TerminalView，挂载即建立会话、卸载即关闭） */
function useTerminals(aid: string, setActive: (id: string | null) => void) {
  const { t } = useT();
  const [list, setList] = useState<number[]>([]);
  const seq = useRef(0);
  const open = () => {
    const n = ++seq.current;
    setList((xs) => [...xs, n]);
    setActive(`term:${n}`);
  };
  const close = (n: number) => {
    setList((xs) => xs.filter((x) => x !== n));
    setActive(null);
  };
  const tabs: ExtraTab[] = list.map((n) => ({
    item: { id: `term:${n}`, label: t("term.tab", { n }), icon: "terminal", closeLabel: t("term.close", { n }) },
    render: () => <TerminalView key={n} aid={aid} />,
    onClose: () => close(n),
  }));
  return { tabs, open };
}
